/**
 * Custom mdast node types for card transformations
 *
 * These types extend the standard mdast (Markdown Abstract Syntax Tree)
 * to support our custom card structures for both grouped cards and single cards.
 */

import type { Blockquote, Parent } from 'mdast';

/**
 * Card type identifier for single cards
 */
export type CardType = 'win' | 'warning' | 'quote' | 'note';

/**
 * Grouped cards container node
 *
 * Represents a `<div class="grouped-cards">` wrapper containing multiple blockquote cards.
 * This is the format generated by LLMs for related cards that should be visually grouped.
 *
 * @example
 * ```markdown
 * <div class="grouped-cards">
 * > ✅ **Win:** Closed 3 deals
 * > ⚠️ **Warning:** Pipeline needs attention
 * </div>
 * ```
 */
export interface GroupedCardsNode extends Parent {
  type: 'grouped_cards';
  children: Blockquote[];
  data?: {
    hName: string;
    hProperties: {
      className: string[];
    };
  };
}

/**
 * Single card node
 *
 * Represents an emoji-prefixed blockquote that should render as a styled card.
 * Cards can be standalone or nested within a grouped_cards container.
 *
 * @example
 * ```markdown
 * > ✅ **Win:** Achievement unlocked
 * > ⚠️ **Warning:** Action required
 * > 💬 **Quote:** "Success is a journey"
 * > 📝 **Note:** Remember to follow up
 * ```
 */
export interface CardNode extends Parent {
  type: 'card';
  cardType: CardType;
  children: Blockquote['children'];
  data?: {
    hName: string;
    hProperties: {
      className: string[];
      'data-card-type': CardType;
    };
  };
}

/**
 * Type guard for grouped_cards nodes
 */
export function isGroupedCardsNode(node: unknown): node is GroupedCardsNode {
  return (
    typeof node === 'object' && node !== null && 'type' in node && node.type === 'grouped_cards'
  );
}

/**
 * Type guard for card nodes
 */
export function isCardNode(node: unknown): node is CardNode {
  return (
    typeof node === 'object' &&
    node !== null &&
    'type' in node &&
    node.type === 'card' &&
    'cardType' in node
  );
}

/**
 * Emoji prefixes that identify card types
 */
export const CARD_EMOJI_MAP: Record<CardType, string[]> = {
  win: ['✅', '🎉', '🏆', '💯'],
  warning: ['⚠️', '🚨', '⚡'],
  quote: ['💬', '💭', '🗨️'],
  note: ['📝', '📌', '💡', 'ℹ️'],
};

/**
 * Card label patterns that identify card types
 * Case-insensitive matching
 */
export const CARD_LABEL_MAP: Record<CardType, string[]> = {
  win: ['win', 'success', 'achievement'],
  warning: ['warning', 'alert', 'caution'],
  quote: ['quote', 'said'],
  note: ['note', 'info', 'reminder'],
};

/**
 * Detect card type from blockquote content
 *
 * @param text - The text content of the blockquote
 * @returns The detected card type, or null if not a card
 */
export function detectCardType(text: string): CardType | null {
  const trimmed = text.trim();

  // Check emoji prefixes first
  for (const [type, emojis] of Object.entries(CARD_EMOJI_MAP)) {
    if (emojis.some((emoji) => trimmed.startsWith(emoji))) {
      return type as CardType;
    }
  }

  // Check label patterns (after emoji or bold marker)
  const lowerText = trimmed.toLowerCase();
  for (const [type, labels] of Object.entries(CARD_LABEL_MAP)) {
    if (
      labels.some((label) => {
        // Match patterns like: "**Win:**", "Win:", etc.
        const pattern = new RegExp(`^[^a-z]*${label}\\s*:`, 'i');
        return pattern.test(lowerText);
      })
    ) {
      return type as CardType;
    }
  }

  return null;
}
